import Analytics from '@/utils/analytics';
import i18n from '@/utils/i18n';
import moment from 'moment-timezone';
import React, { useCallback } from 'react';
import { APP_MODAL_ROUTES } from '@/navigators/routes';
import { AppModalStackParams } from '@/navigators/types';
import {
  DaysOfWeek,
  UpsertRestaurantVp_SubscriptionMutation,
  UpsertRestaurantVp_SubscriptionMutationVariables,
} from '@/types';
import {
  DEFAULT_CURRENCY_CODE,
  displayCurrencyPlaceholder,
  formatCurrency,
} from '@/utils/money';
import {
  GET_RESTAURANT_VP_SUBSCRIPTIONS,
  UPSERT_RESTAURANT_SUBSCRIPTION_MUTATION,
} from './subscriptions/queries';
import { getOperationName } from 'apollo-utilities';
import { ORDER_FULFILLMENT_TYPE_TO_TRANSLATION_KEY_MAP } from '@domain/fulfillment-settings/blocs/order-availability';
import { StackScreenProps } from '@react-navigation/stack';
import {
  SubscriptionForm,
  SubscriptionFormValues,
} from '@/components/subscriptions/subscription-form';
import { useAuthenticatedUserContext } from '@/contexts/user-context';
import { useMutation } from '@apollo/client';
import { useNewOrderContext } from '@/contexts/new-order-context';
import { useToasts } from '@/contexts/toast-context';

type Props = StackScreenProps<
  AppModalStackParams,
  APP_MODAL_ROUTES.EditSubscription
>;

export const EditSubscriptionScreen: React.FC<Props> = ({
  navigation,
  route,
}) => {
  const {
    params: {
      frequencyDays,
      fulfillmentMethodUUID,
      metadata,
      nextAvailableDate,
      orderDay,
      quantity,
      restaurantVPSubscriptionUUID,
    },
  } = route;

  const { availableOrderFulfillments } = useNewOrderContext();
  const {
    selectedRestaurantUUID: restaurantUUID,
    selectedRestaurantRelationship: { supplierUUID },
  } = useAuthenticatedUserContext();
  const { showErrorToast } = useToasts();

  const [upsertRestaurantVPSubscription, { loading }] = useMutation<
    UpsertRestaurantVp_SubscriptionMutation,
    UpsertRestaurantVp_SubscriptionMutationVariables
  >(UPSERT_RESTAURANT_SUBSCRIPTION_MUTATION, {
    refetchQueries: [
      getOperationName(GET_RESTAURANT_VP_SUBSCRIPTIONS) as string,
    ],
  });

  const currentFulfillmentMethod = availableOrderFulfillments.find(
    (item) => item.fulfillment_settings_id === fulfillmentMethodUUID,
  );

  const initialValues = {
    frequencyDays: {
      title: i18n.t(`frequency_${frequencyDays}_days`),
      value: frequencyDays,
    },
    fulfillmentMethod: currentFulfillmentMethod
      ? {
          availability: currentFulfillmentMethod.availability,
          id: fulfillmentMethodUUID,
          title:
            currentFulfillmentMethod.fulfillment_display_name ||
            ORDER_FULFILLMENT_TYPE_TO_TRANSLATION_KEY_MAP[
              currentFulfillmentMethod.fulfillment_name
            ],
          value: currentFulfillmentMethod.fulfillment_name,
        }
      : null,
    orderDay: {
      date: nextAvailableDate,
      title: moment(nextAvailableDate).format('dddd'),
      value: orderDay.toUpperCase() as DaysOfWeek,
    },
    quantity,
  };

  const onSubmit = useCallback(
    async (values: SubscriptionFormValues) => {
      // we will have all values after a CTA button disabled check
      const { frequencyDays, fulfillmentMethod, orderDay, quantity } = values;

      Analytics.track('edit_subscription.button', {
        subscriptionUUID: restaurantVPSubscriptionUUID,
        variantPackUUID: metadata.variantPackUUID,
      });

      try {
        await upsertRestaurantVPSubscription({
          variables: {
            frequencyDays: frequencyDays!.value,
            fulfillmentSettingsUUID: fulfillmentMethod!.id,
            orderDay: orderDay!.value,
            quantity,
            restaurantUUID,
            restaurantVPSubscriptionUUID,
            supplierUUID,
            variantPackUUID: metadata.variantPackUUID,
          },
        });
        navigation.goBack();
      } catch {
        showErrorToast();
      }
    },
    [
      metadata.variantPackUUID,
      navigation,
      restaurantUUID,
      restaurantVPSubscriptionUUID,
      showErrorToast,
      supplierUUID,
      upsertRestaurantVPSubscription,
    ],
  );

  const currencyCode = metadata.currencyCode ?? DEFAULT_CURRENCY_CODE;

  const price = metadata.unitPriceInMicros
    ? formatCurrency(metadata.unitPriceInMicros, currencyCode)
    : displayCurrencyPlaceholder(currencyCode);

  return (
    <SubscriptionForm
      buttonLabel={i18n.t('save')}
      initialValues={initialValues}
      loading={loading}
      maximumQuantity={metadata.maximumQuantity}
      message={i18n.t('subscription_settings.message')}
      minimumQuantity={metadata.minimumQuantity}
      onClose={navigation.goBack}
      onSubmit={onSubmit}
      price={price}
      subscriptionUUID={restaurantVPSubscriptionUUID}
      title={i18n.t('subscription_settings')}
    />
  );
};
